import { cn } from '@/lib/utils';

interface AvatarGradientConfig {
  from: string;
  to: string;
  textColor: string;
}

// Predefined gradient combinations for consistent visuals
const gradientPresets: AvatarGradientConfig[] = [
  { from: 'from-teal-500', to: 'to-cyan-500', textColor: 'text-white' },
  { from: 'from-blue-500', to: 'to-indigo-500', textColor: 'text-white' },
  { from: 'from-violet-500', to: 'to-purple-500', textColor: 'text-white' },
  { from: 'from-fuchsia-500', to: 'to-pink-500', textColor: 'text-white' },
  { from: 'from-rose-500', to: 'to-orange-500', textColor: 'text-white' },
  { from: 'from-amber-500', to: 'to-yellow-500', textColor: 'text-navy' },
  { from: 'from-emerald-500', to: 'to-teal-500', textColor: 'text-white' },
  { from: 'from-cyan-500', to: 'to-blue-500', textColor: 'text-white' },
  { from: 'from-indigo-500', to: 'to-violet-500', textColor: 'text-white' },
  { from: 'from-purple-500', to: 'to-fuchsia-500', textColor: 'text-white' },
  { from: 'from-pink-500', to: 'to-rose-500', textColor: 'text-white' },
  { from: 'from-orange-500', to: 'to-amber-500', textColor: 'text-navy' },
];

/**
 * Get a consistent gradient configuration based on a string identifier
 * This ensures the same user always gets the same gradient
 */
export function getGradientForId(id: string | undefined): AvatarGradientConfig {
  if (!id) return gradientPresets[0];
  
  // Simple hash function to get consistent index
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  const index = Math.abs(hash) % gradientPresets.length;
  return gradientPresets[index];
}

/**
 * Get initials from a name
 */
export function getInitials(firstName?: string, lastName?: string): string {
  if (!firstName && !lastName) return '?';
  const first = firstName?.[0] || '';
  const last = lastName?.[0] || '';
  return (first + last).toUpperCase() || firstName?.slice(0, 2).toUpperCase() || '?';
}

interface AvatarProps {
  firstName?: string;
  lastName?: string;
  userId?: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-16 h-16 text-lg',
  xl: 'w-24 h-24 text-2xl',
};

export function GradientAvatar({ 
  firstName, 
  lastName, 
  userId, 
  src, 
  size = 'md',
  className 
}: AvatarProps) {
  const initials = getInitials(firstName, lastName);
  const gradient = getGradientForId(userId || `${firstName}${lastName}`);
  
  if (src) {
    return (
      <img
        src={src}
        alt={`${firstName} ${lastName}`}
        className={cn(
          'rounded-full object-cover',
          sizeClasses[size],
          className
        )}
      />
    );
  }
  
  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center font-medium bg-gradient-to-br',
        gradient.from,
        gradient.to,
        gradient.textColor,
        sizeClasses[size],
        className
      )}
    >
      {initials}
    </div>
  );
}

export default GradientAvatar;
